import store from '../../../store/store';
import { OffCanvasConfig } from '../../off-canvas/interfaces/off-canvas.interface';
import { WISH_LIST_OFF_CANVAS_IDS } from '../interfaces/wish-list-off-canvas.interface';
import { WishList } from '../interfaces/wish-list-items.interface';
import { html, TemplateResult } from 'lit';
import { sendOffCanvasData, triggerOpenOffCanvas } from '../../off-canvas/store/off-canvas.store';
import { wishListReviewDropdownButtons } from './wish-list.util';
import { COMMON_OFF_CANVAS_IDS } from '../../off-canvas/interfaces/common-off-canvas.interface';

/**
 * Dispatches the Redux action to trigger the create wishlist off-canvas component.
 */
export const openCreateWishListOffCanvas = (): void => {
  store.dispatch(
    triggerOpenOffCanvas({
      offCanvasId: WISH_LIST_OFF_CANVAS_IDS.CREATE_WISH_LIST_MY_LISTS,
      triggerId: `#create-wish-list-button`,
    })
  );
};

/**
 * Dispatches the Redux action to trigger the update wishlist off-canvas component.
 */
export const openUpdateWishListOffCanvas = (wishList: WishList): void => {
  store.dispatch(
    sendOffCanvasData({
      offCanvasId: WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST_MY_LISTS,
      data: wishList,
    })
  );
  store.dispatch(
    triggerOpenOffCanvas({
      offCanvasId: WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST_MY_LISTS,
      triggerId: `#wish-list-dropdown-edit-list-name-option-${wishList.wishlist_id}`,
    })
  );
};

/**
 * Dispatches the Redux action to trigger the delete wishlist off-canvas component.
 */
export const openDeleteWishListOffCanvas = (wishListId: number): void => {
  store.dispatch(
    triggerOpenOffCanvas({
      offCanvasId: WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST_MY_LISTS,
      triggerId: `#wish-list-dropdown-delete-option-${wishListId}`,
    })
  );
};

/**
 * Dispatches the Redux action to trigger the download wishlist off-canvas component.
 */
export const openDownloadWishListOffCanvas = (wishList: WishList): void => {
  store.dispatch(
    sendOffCanvasData({
      offCanvasId: WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_ITEMS,
      data: wishList,
    })
  );
  store.dispatch(
    triggerOpenOffCanvas({
      offCanvasId: WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_ITEMS,
      triggerId: `#wish-list-dropdown-download-option-${wishList.wishlist_id}`,
    })
  );
};

/**
 * Dispatches the Redux action to trigger the share wishlist off-canvas component.
 */
export const openShareWishListOffCanvas = (wishListId: number): void => {
  store.dispatch(
    triggerOpenOffCanvas({
      offCanvasId: COMMON_OFF_CANVAS_IDS.COPY_AND_SHARE_LINK,
      triggerId: `#wish-list-dropdown-share-option-${wishListId}`,
    })
  );
};

/**
 * Returns the header template for the wish list review off-canvas.
 *
 * @param wishList
 */
export const getWishListReviewCanvasHeader = (wishList?: WishList): TemplateResult => {
  if (!wishList) {
    return html``;
  }

  return html`
    <div class="row g-0 m-0 w-100">
      <div class="col-10 align-content-center">
        <h5 class="mb-0">${wishList?.name}</h5>
      </div>
      <div class="col col-2 flex-grow-1 text-end">
        <mhecomm-wish-list-dropdown-options
          .dropDownItems=${wishListReviewDropdownButtons()}
          .dropDownView=${true}
          .offCanvasId=${WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST}
          .wishList=${wishList}
        ></mhecomm-wish-list-dropdown-options>
      </div>
    </div>
  `;
};

/**
 * Returns the wish list from the event detail when found.
 *
 * @param event
 */
export const getWishListFromEvent = (event: CustomEventInit): WishList | undefined => {
  const wishList = event?.detail?.wishList;
  if (!wishList) {
    console.error('Wish list not found, the action could not be performed.');
    return undefined;
  }

  return wishList;
};

/**
 * Returns the configuration for the update wish list off-canvas.
 *
 * @param id
 * @param prevId
 * @param title
 */
export const getUpdateWishListConfig = (id: string, prevId: string, title: string): OffCanvasConfig => {
  return {
    id: id,
    title: html`<h5 class="mb-0">${title}</h5>`,
    closeTriggerId: 'wish-list-product-done-button',
    focusElement: '#editWishListName',
    prevTrigger: {
      triggerId: '#cancelWishlistUpdationBtn',
      offCanvasId: prevId,
    },
  };
};

/**
 * Returns the configuration for the delete wish list off-canvas.
 *
 * @param id
 * @param prevId
 * @param titlePrefix
 * @param wishList
 */
export const getDeleteWishListConfig = (
  id: string,
  prevId: string,
  titlePrefix: string,
  wishList?: WishList
): OffCanvasConfig => {
  return {
    id: id,
    closeTriggerId: 'wish-list-product-done-button',
    prevTrigger: {
      triggerId: `#delete-wish-list-${wishList?.wishlist_id}-cancel`,
      offCanvasId: prevId,
    },
    title: html`
      <div>
        ${titlePrefix} ${wishList ? html` named <span class="fw-bold">${wishList.name}</span>` : ''}?
      </div>
    `,
  };
};

/**
 * Returns the configuration for the download wish list off-canvas.
 *
 * @param id
 * @param prevId
 * @param title
 */
export const getDownloadWishListConfig = (
  id: string,
  prevId: string,
  title: string,
): OffCanvasConfig => {
  return {
    id: id,
    prevTrigger: {
      offCanvasId: prevId,
    },
    title: html`<h5 class="mb-0">${title}</h5>`,
  };
};
